import {useEffect, useRef, useState} from "react";
import ProjectCover from "./components/ProjectCover/ProjectCover";
import './style.scss'
import Image from "./components/Image/Image";
import Lightbox from "../Lightbox/Lightbox";
import {useLocation} from "react-router-dom";

const Gallery = ({
    images = [],
    onClick,
    isProjectCover,
    route: routeOverride,
    animated = false,
    lightbox = false
}) => {
    const route = useLocation();
    const currentPath = routeOverride || route.pathname.replace(/\//g, "");
    const [isMobile, setIsMobile] = useState(() => (
        typeof window !== 'undefined' && window.innerWidth <= 600
    ));
    const shouldAnimate = animated && (isMobile || images.length >= 4);
    const [activeIndex, setActiveIndex] = useState(0);
    const [showLightbox, setShowLightbox] = useState(false);
    const [lightboxData, setLightboxData] = useState({index: null, src: ''});
    const touchStartX = useRef(null);
    const visibleImages = images;

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 600);

        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        setActiveIndex(0);
    }, [shouldAnimate, images]);

    useEffect(() => {
        if (!shouldAnimate) return undefined;

        const handleKeyDown = event => {
            if (event.key === 'ArrowLeft') {
                setActiveIndex(index => (index - 1 + visibleImages.length) % visibleImages.length);
            }
            if (event.key === 'ArrowRight') {
                setActiveIndex(index => (index + 1) % visibleImages.length);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [activeIndex, shouldAnimate, visibleImages.length]);

    const moveTo = index => {
        if (visibleImages.length === 0) return;
        setActiveIndex((index + visibleImages.length) % visibleImages.length);
    };

    const handleClick = (index, src) => {
        if (shouldAnimate && index !== activeIndex) {
            moveTo(index);
            return true;
        }

        if (lightbox) {
            setLightboxData({index, src});
            setShowLightbox(true);
            return true;
        }

        onClick?.(index, src);
        return false;
    };

    const handleTouchStart = event => {
        if (shouldAnimate) touchStartX.current = event.touches[0].clientX;
    };

    const handleTouchEnd = event => {
        if (!shouldAnimate || touchStartX.current === null) return;

        const touchDistance = event.changedTouches[0].clientX - touchStartX.current;
        touchStartX.current = null;

        if (Math.abs(touchDistance) < 45) return;
        moveTo(activeIndex + (touchDistance < 0 ? 1 : -1));
    };

    if (images.length === 0) return null;

    return (
        <div
            className={`gallery ${shouldAnimate ? 'gallery--animated' : ''} ${lightbox ? 'has-lightbox' : ''}`}
            onTouchStart={shouldAnimate ? handleTouchStart : undefined}
            onTouchEnd={shouldAnimate ? handleTouchEnd : undefined}
        >
            {lightbox && showLightbox && (
                <Lightbox
                    data={lightboxData}
                    gallery={images}
                    closeCB={() => setShowLightbox(false)}
                />
            )}

            {visibleImages.map((image, index) => {
            const {public_id} = image;

            if (!shouldAnimate) {
                return (isProjectCover
                    ? <ProjectCover key={index}
                               index={index}
                               src={public_id}
                               image={image}
                               route={currentPath}
                               onClick={onClick}/>
                    : <Image key={index} src={public_id} index={index} onClick={onClick}/>);
            }

            const distance = index - activeIndex;
            const normalizedDistance = Math.abs(distance) > 2
                ? distance > 0 ? 3 : -3
                : distance;

            return (isProjectCover
                ? <button className={`gallery__item distance-${normalizedDistance}`} key={index} type="button"
                          onClick={() => handleClick(index, public_id)} aria-label="Show image">
                    <ProjectCover
                        src={public_id}
                        image={image}
                        route={currentPath}
                        onClick={() => handleClick(index, public_id)}
                    />
                </button>
                : <button className={`gallery__item distance-${normalizedDistance}`} key={index} type="button"
                          onClick={() => handleClick(index, public_id)} aria-label="Show image">
                    <Image src={public_id}/>
                </button>);
            })}

            {shouldAnimate && visibleImages.length > 1 && <>
                <button className="gallery__arrow gallery__arrow--previous" type="button"
                        onClick={() => moveTo(activeIndex - 1)} aria-label="Previous image">&#8249;</button>
                <button className="gallery__arrow gallery__arrow--next" type="button"
                        onClick={() => moveTo(activeIndex + 1)} aria-label="Next image">&#8250;</button>
            </>}
        </div>
    )
};

export default Gallery;
